import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useStore } from '../../store';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { createLocalRecord, db, withExtras, withFormulas, withParents } from '../../Utils/Functions/Dexie-Setup';
import { useLiveQuery } from 'dexie-react-hooks';
import SRFButton from '../Inputs/SRFButton/SRFButton';
import DateInput from '../Inputs/Date-Input';
import RelationshipInput from '../Inputs/RelationshipInput';
import Loader from '../elements/Loader/Loader';
import TextInput from '../Inputs/Text-Input';
import SwitchInput from '../Inputs/SwitchInput';
import TextMultiLineInput from '../Inputs/Text-Multi-line-Input';
import SignatureInput from '../Inputs/SignatureInput';
import DataSheetInput from '../Inputs/DatasheetInput';
import MultipleChoiceInput from '../Inputs/Multiple-Choice-Input';
import { createAddRowsComponent } from 'react-datasheet-grid';

const DeleteColumn = {
  component: ({ deleteRow }) => (
    <button type='button' className='text-center w-full h-full' onClick={deleteRow}>❌</button>
  ),
  basis: 50,
}

function Form() {
  const { isAuthed, isOnline, isSpanish, translation } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const createdRid = useRef(false);
  const laborRef = useRef(null);
  const equipmentRef = useRef(null);
  const standardEquipmentRef = useRef(null);

  const AddRows = useMemo(() => createAddRowsComponent({
    button: translation.add,
    unit: translation.rows,
  }), [translation]);

  const key = useMemo(() => Number(params?.key) || Number(atob(params?.key || "") ?? 0), [params]);

  const [currentSubmission, loaded] = useLiveQuery(() =>
    db.dailyFieldReports.get(key)
      .then(record => record ? withExtras("dailyFieldReports", [withParents, withFormulas])([record]) : undefined)
      .then(res => res ? res[0] : undefined)
      .then(res => [res, true]), [key], [undefined, false]);

  const standardEquipmentArray = useLiveQuery(() =>
    db.equipment.filter((eq) => eq.standardItem).primaryKeys());

  const standardEquipmentSet = useMemo(() => new Set(standardEquipmentArray), [standardEquipmentArray]);

  const submitRecord = useCallback(async (e) => {
    e.preventDefault();
    await Promise.all([
      laborRef.current?.forceSave(),
      equipmentRef.current?.forceSave(),
      standardEquipmentRef.current?.forceSave()
    ]);
    const nextSubmitted = !currentSubmission?.submitted;
    db.dailyFieldReports.update(key, { submitted: nextSubmitted, locallyUpdatedAt: Date.now() });
    if (nextSubmitted) {
      navigate("/");
    }
  }, [currentSubmission, key]);

  const laborFilter = useCallback((laborItem) => laborItem.relatedDailyFieldReport === key, [key]);
  const laborDefault = useMemo(() => ({
    relatedDailyFieldReport: key,
    startTime: "07:00:00",
    endTime: "16:00:00",
    position: "Flagger"
  }), [key]);

  const laborColumns = useMemo(() => [
    ["relatedEmployee", { title: translation.employee, disabled: key > 0, displayFields: ["name"], isCellEmpty: () => true, required: true }],
    ["position", { title: translation.position, disabled: key > 0, isCellEmpty: ({ rowData }) => rowData }],
    ["driver", { title: translation.driver, disabled: key > 0, isCellEmpty: () => true }],
    ["startTime", { title: translation.startTime, disabled: key > 0, isCellEmpty: () => true }],
    ["endTime", { title: translation.endTime, disabled: key > 0, isCellEmpty: () => true }],
  ], [translation, key]);

  const standardEquipmentFilter = useCallback((equipment) => equipment.relatedDailyFieldReport === key && standardEquipmentSet.has(equipment?.relatedEquipment), [key, standardEquipmentSet]);

  const standardEquipmentDefault = useMemo(() => ({ relatedDailyFieldReport: key }), [key]);

  const standardEquipmentColumns = useMemo(() => [
    ["relatedEquipmentName", { title: translation.equipment, disabled: key > 0, isCellEmpty: () => false }],
    ["quantity", { title: translation.quantity, disabled: key > 0, isCellEmpty: () => false }]
  ], [translation, key]);

  const equipmentFilter = useCallback((equipment) => equipment.relatedDailyFieldReport === key && !standardEquipmentSet.has(equipment?.relatedEquipment), [key, standardEquipmentSet]);

  const equipmentDefault = useMemo(() => ({ relatedDailyFieldReport: key }), [key]);

  const equipmentColumns = useMemo(() => [
    ["relatedEquipment", { title: translation.equipment, disabled: key > 0, displayFields: ["equipmentName"], isCellEmpty: () => true }],
    ["quantity", { title: translation.quantity, disabled: key > 0, isCellEmpty: () => true }]
  ], [translation, key]);

  useEffect(() => {
    if (!isAuthed) {
      navigate("/auth");
    }
  }, [isAuthed]);

  useEffect(() => {
    if ((key && loaded) && !currentSubmission) {
      const timeout = setTimeout(() => navigate("/", { replace: true }), 4000);
      return () => clearTimeout(timeout);
    }
  }, [key, loaded, currentSubmission]);

  useEffect(() => {
    if (!key && !createdRid.current) {
      const defaultValues = {
        date: new Date().toISOString().split('T')[0],
        status: "Submitted"
      };
      createdRid.current = true;
      searchParams.forEach((value, key) => defaultValues[key] = value);
      (async () => {
        const newKey = await createLocalRecord("dailyFieldReports", defaultValues);

        const standardEquipment = await db.equipment
          .filter(eq => eq?.standardItem)
          .toArray();
        await Promise.all(standardEquipment.map(eq => createLocalRecord("equipmentLineItems", { relatedDailyFieldReport: newKey, relatedEquipment: eq?.recordId, standardItem: true, quantity: 0 })));

        navigate(`/form/${btoa(newKey)}`, { replace: true });
      })();
    }
  }, [key]);

  if (currentSubmission) {
    return (
      <form className='flex flex-col items-center py-4 gap-6' onSubmit={submitRecord}>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <Link to="/">
            <SRFButton color='#0f314d'>{translation.home}</SRFButton>
          </Link>
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <DateInput
            required
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"date"}
            label={translation.date}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <RelationshipInput
            required
            disabled={key > 0}
            tableName={"jobs"}
            fieldValue={currentSubmission?.jobNumber ?? null}
            setFieldValue={(jobNumber) => db.dailyFieldReports.update(key, { jobNumber: jobNumber })}
            fieldName={"jobNumber"}
            label={translation.job}
            displayFields={["jobNumber", "jobName"]}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"workOrderNumber"}
            label={translation.workOrderNum}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <SwitchInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"rainout"}
            label={translation.rainedOut}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextMultiLineInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"notes"}
            label={translation.notes}
          />
        </div>
        <h3 className='text-lg'>{translation.location}</h3>
        {
          !(currentSubmission?.jobNumber_record?.location) ?
            <>
              <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
                <TextInput
                  disabled={key > 0}
                  tableName={"dailyFieldReports"}
                  primaryKey={key}
                  fieldName={"street1"}
                  label={translation.street1}
                />
              </div>
              <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
                <TextInput
                  disabled={key > 0}
                  tableName={"dailyFieldReports"}
                  primaryKey={key}
                  fieldName={"street2"}
                  label={translation.street2}
                />
              </div>
              <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
                <TextInput
                  disabled={key > 0}
                  tableName={"dailyFieldReports"}
                  primaryKey={key}
                  fieldName={"city"}
                  label={translation.city}
                />
              </div>
              <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
                <MultipleChoiceInput
                  disabled={key > 0}
                  tableName={"dailyFieldReports"}
                  primaryKey={key}
                  fieldName={"state"}
                  label={translation.state}
                />
              </div>
              <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
                <TextInput
                  disabled={key > 0}
                  tableName={"dailyFieldReports"}
                  primaryKey={key}
                  fieldName={"postalCode"}
                  label={translation.postalCode}
                />
              </div>
            </>
            :
            <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
              <TextInput
                disabled
                tableName={"jobs"}
                primaryKey={currentSubmission?.jobNumber ?? ""}
                fieldName={"location"}
                label={translation.jobLocation}
              />
            </div>
        }
        <h3 className='text-lg'>{translation.laborAndEquipment}</h3>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            ref={laborRef}
            tableName={"laborLineItems"}
            filter={laborFilter}
            defaultValues={laborDefault}
            columns={laborColumns}
            label={translation.laborLineItems}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            ref={standardEquipmentRef}
            tableName={"equipmentLineItems"}
            filter={standardEquipmentFilter}
            defaultValues={standardEquipmentDefault}
            columns={standardEquipmentColumns}
            label={translation.standardEquipment}
            lockRows
          />
        </div>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            ref={equipmentRef}
            tableName={"equipmentLineItems"}
            filter={equipmentFilter}
            defaultValues={equipmentDefault}
            columns={equipmentColumns}
            label={translation.additionalEquipment}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <h3 className='text-lg'>{translation.signOff}</h3>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"foremanName"}
            label={translation.foremanName}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <SignatureInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"foremanSignature"}
            label={translation.foremanSignature}
            buttonLabels={translation}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <TextInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"inspectorName"}
            label={translation.inspectorName}
          />
        </div>
        <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
          <SignatureInput
            disabled={key > 0}
            tableName={"dailyFieldReports"}
            primaryKey={key}
            fieldName={"inspectorSignature"}
            label={translation.inspectorSignature}
            buttonLabels={translation}
          />
        </div>
        {
          key < 0 ?
            <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
              <SRFButton type={"submit"} color='#0f314d'>
                {currentSubmission?.submitted ? translation.unsubmit : translation.submit}
              </SRFButton>
            </div>
            : null
        }
      </form>
    )
  } else {
    return <Loader />
  }

}

export default Form;
